import { ColorEnum } from '@/constants/color'
import Button from '@/features/openTabs/components/AccountActionButton'
import useCashCalculatorKeyboard from '@/features/openTabs/components/CashCalculatorModal/hooks/useCashCalculatorKeyboard'
import SummaryRow from '@/features/openTabs/components/CashCalculatorModal/parts/SummaryRow'
import { useOpenTabModalBack } from '@/features/openTabs/hooks/useOpenTabModalBack'
import BackspaceIcon from '@/features/openTabs/icons/BackspaceIcon'
import Modal from '@/features/ui/components/Modal'
import Typography from '@/features/ui/typography'
import { useScreenLayout } from '@/hooks/screen'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, ScrollView, View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface CashCalculatorModalProps {
    currency: string
    totalPrice: string
    receivedPrice: string
    remainingPrice: string
    isCovered: boolean
    canConfirm: boolean
    helper?: boolean
    errorMessage?: string
    submitting?: boolean
    onQuickAmount: (amount: number) => void
    onDigit: (digit: string) => void
    onBackspace: () => void
    onCancel: () => void
    onConfirm: () => void
}

const quickAmountRows: Record<string, readonly (readonly number[])[]> = {
    CZK: [
        [100, 200, 500],
        [1000, 2000, 5000],
    ],
    EUR: [
        [5, 10, 20],
        [50, 100, 200],
    ],
}
const keypadRows = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['0', ',', 'backspace'],
] as const

const CashCalculatorModal: FC<CashCalculatorModalProps> = ({
    currency,
    totalPrice,
    receivedPrice,
    remainingPrice,
    isCovered,
    canConfirm,
    helper = false,
    errorMessage,
    submitting = false,
    onQuickAmount,
    onDigit,
    onBackspace,
    onCancel,
    onConfirm,
}) => {
    const { t } = useTranslation('app')
    useOpenTabModalBack(onCancel, { disabled: submitting })
    const layout = useScreenLayout()
    const { styles } = useStyles(stylesheet, {
        layout: layout === 'compact' ? 'compact' : undefined,
    })

    const keyboardRef = useCashCalculatorKeyboard({
        disabled: submitting,
        onDigit,
        onBackspace,
    })

    return (
        <Modal contentStyle={styles.modal}>
            <View ref={keyboardRef} style={styles.keyboardContainer}>
                <ScrollView
                    contentContainerStyle={styles.content}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                    style={styles.scroll}
                    testID="open-account-cash-calculator"
                >
                    <View style={styles.summary}>
                        <SummaryRow
                            emphasized
                            label={
                                isCovered
                                    ? t('openTabs.summary.total')
                                    : t('openTabs.payment.totalDue')
                            }
                            value={totalPrice}
                        />
                        <SummaryRow
                            label={
                                isCovered
                                    ? t('openTabs.payment.changeDue')
                                    : t('openTabs.payment.remainingDue')
                            }
                            value={remainingPrice}
                            valueColor={isCovered ? ColorEnum.EMERALD_700 : ColorEnum.BRICK_500}
                        />
                    </View>

                    <View style={styles.calculator}>
                        <View style={styles.quickAmountSection}>
                            <Typography.Heading variant="h5">
                                {t('openTabs.payment.receivedAmount')}
                            </Typography.Heading>
                            <View style={styles.quickAmountRows}>
                                {(quickAmountRows[currency] ?? []).map((row) => (
                                    <View style={styles.quickAmountRow} key={row[0]}>
                                        {row.map((amount) => (
                                            <Pressable
                                                accessibilityRole="button"
                                                disabled={submitting}
                                                key={amount}
                                                onPress={() => onQuickAmount(amount)}
                                                style={styles.quickAmount}
                                            >
                                                <Typography.Description font="bold">
                                                    {amount}
                                                </Typography.Description>
                                            </Pressable>
                                        ))}
                                    </View>
                                ))}
                            </View>
                        </View>
                        <View style={styles.keypadSection}>
                            <View style={styles.receivedPrice}>
                                <Typography.Heading
                                    variant="h2"
                                    numberOfLines={1}
                                    adjustsFontSizeToFit
                                    minimumFontScale={0.5}
                                >
                                    {receivedPrice}
                                </Typography.Heading>
                            </View>
                            <View style={styles.keypadRows}>
                                {keypadRows.map((row) => (
                                    <View style={styles.keypadRow} key={row[0]}>
                                        {row.map((key) =>
                                            key === 'backspace' ? (
                                                <Pressable
                                                    accessibilityLabel={t(
                                                        'openTabs.payment.backspace'
                                                    )}
                                                    accessibilityRole="button"
                                                    disabled={submitting}
                                                    key={key}
                                                    onPress={onBackspace}
                                                    style={[styles.key, styles.backspaceKey]}
                                                >
                                                    <BackspaceIcon color={ColorEnum.JET_100} />
                                                </Pressable>
                                            ) : (
                                                <Pressable
                                                    accessibilityRole="button"
                                                    disabled={submitting}
                                                    key={key}
                                                    onPress={() => onDigit(key)}
                                                    style={styles.key}
                                                >
                                                    <Typography.Description
                                                        size="large"
                                                        font="bold"
                                                    >
                                                        {key}
                                                    </Typography.Description>
                                                </Pressable>
                                            )
                                        )}
                                    </View>
                                ))}
                            </View>
                        </View>
                    </View>
                </ScrollView>
                <View style={styles.footer}>
                    {errorMessage && (
                        <Typography.Description style={styles.error}>
                            {errorMessage}
                        </Typography.Description>
                    )}
                    <View style={styles.actions}>
                        {helper ? (
                            <Button
                                color="action"
                                onPress={onCancel}
                                style={styles.button}
                                testID="open-account-calculator-close"
                                disabled={submitting}
                            >
                                {t('actions.close')}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    color="primary"
                                    disabled={submitting}
                                    onPress={onCancel}
                                    style={styles.button}
                                    testID="open-account-cash-cancel"
                                >
                                    {t('openTabs.actions.cancel')}
                                </Button>
                                <Button
                                    color="action"
                                    disabled={!canConfirm || submitting}
                                    onPress={onConfirm}
                                    style={styles.button}
                                    testID="open-account-cash-confirm"
                                >
                                    {t('openTabs.payment.confirm')}
                                </Button>
                            </>
                        )}
                    </View>
                </View>
            </View>
        </Modal>
    )
}

const stylesheet = createStyleSheet({
    modal: {
        width: 480,
        maxWidth: '93%',
        overflow: 'hidden',
    },
    keyboardContainer: {
        flexShrink: 1,
        minHeight: 0,
    },
    scroll: {
        flexShrink: 1,
        minHeight: 0,
    },
    content: {
        paddingTop: 32,
        paddingHorizontal: 32,
        paddingBottom: 16,
        gap: 24,
        variants: {
            layout: {
                compact: {
                    paddingTop: 16,
                    paddingHorizontal: 16,
                    gap: 16,
                },
            },
        },
    },
    summary: {
        gap: 8,
        paddingBottom: 8,
        borderBottomWidth: 1,
        borderBottomColor: ColorEnum.GRAY_300,
    },
    calculator: {
        gap: 16,
    },
    quickAmountSection: {
        gap: 8,
    },
    quickAmountRows: {
        gap: 12,
    },
    quickAmountRow: {
        width: '100%',
        flexDirection: 'row',
        gap: 8,
    },
    quickAmount: {
        flex: 1,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        backgroundColor: ColorEnum.VIOLET_200,
        variants: {
            layout: {
                compact: {
                    height: 44,
                },
            },
        },
    },
    receivedPrice: {
        height: 60,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: ColorEnum.GRAY_300,
        borderRadius: 8,
        backgroundColor: ColorEnum.WHITE,
        variants: {
            layout: {
                compact: {
                    height: 52,
                },
            },
        },
    },
    keypadSection: {
        gap: 16,
    },
    keypadRows: {
        gap: 8,
    },
    keypadRow: {
        width: '100%',
        height: 60,
        flexDirection: 'row',
        gap: 8,
        variants: {
            layout: {
                compact: {
                    height: 48,
                },
            },
        },
    },
    key: {
        flex: 1,
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: ColorEnum.GRAY_300,
        borderRadius: 8,
        backgroundColor: ColorEnum.GRAY_100,
    },
    backspaceKey: {
        borderWidth: 0,
        backgroundColor: '#ECE9EC',
    },
    footer: {
        flexShrink: 0,
        padding: 16,
        gap: 12,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        backgroundColor: ColorEnum.VIOLET_100,
    },
    actions: {
        flexDirection: 'row',
        gap: 12,
    },
    button: {
        flex: 1,
        minHeight: 44,
        justifyContent: 'center',
        borderRadius: 8,
    },
    error: {
        color: ColorEnum.BRICK_500,
    },
})

export default CashCalculatorModal
